/**
 * Design System Component: Button
 */

import type { ButtonProps as MuiButtonProps } from "@mui/material/Button";
import Loader from "components/LoaderDots";
import { alpha, styled } from "@mui/material";
import MuiButton from "@mui/material/Button";
import { forwardRef, useCallback } from "react";

export interface ButtonProps extends Pick<
  MuiButtonProps,
  | "disabled"
  | "sx"
  | "fullWidth"
  | "href"
  | "size"
  | "onClick"
  | "children"
  | "startIcon"
  | "endIcon"
  | "type"
  | "form"
  | "id"
  | "tabIndex"
  | "component"
  | "onMouseDown"
  | "onFocus"
  | "className"
  | "disableElevation"
  | "autoFocus"
> {
  loading?: boolean;
  variant?: "primary" | "secondary" | "tertiary" | "text" | "danger" | "link" | "hyrox";
}

const Button = styled(
  forwardRef<any, ButtonProps>((props: ButtonProps, ref) => {
    const { variant = "primary", children, loading = false, startIcon, endIcon, onClick, ...rest } = props;

    /** This is to prevent the button from being clicked when loading is true. */
    const handleOnClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (loading) return;
        onClick?.(e);
      },
      [loading, onClick]
    );

    return (
      <MuiButton
        ref={ref}
        startIcon={loading ? null : startIcon}
        endIcon={loading ? null : endIcon}
        data-loading={loading}
        disableElevation
        disableFocusRipple
        variant={variant}
        onClick={handleOnClick}
        aria-busy={loading}
        {...rest}
      >
        {loading ? <Loader size={rest.size} /> : children}
      </MuiButton>
    );
  })
)(({ theme }) => ({
  display: "inline-flex",
  justifyContent: "center",
  alignItems: "center",
  whiteSpace: "nowrap",
  borderRadius: 999,
  transition: "all 100ms ease-out",
  textTransform: "unset",
  minWidth: theme.spacing(4),
  cursor: "pointer",

  "&[data-loading='true']": {
    userSelect: "none",
    cursor: "not-allowed",
  },

  // Sizing
  "&.MuiButton-sizeMedium": {
    padding: theme.spacing(1.75, 3),
    gap: theme.spacing(1),
    height: theme.spacing(6),
  },

  "&.MuiButton-sizeSmall": {
    padding: theme.spacing(1, 2.5),
    gap: theme.spacing(0.75),
    fontSize: "14px",
    lineHeight: "20px",
    height: theme.spacing(4.5),
  },

  "&.MuiButton-sizeExtraSmall": {
    padding: theme.spacing(0.5, 1.75),
    gap: theme.spacing(0.75),
    fontSize: "14px",
    lineHeight: "20px",
    height: theme.spacing(3.5),
  },

  "&.MuiButton-sizeLarge": {
    padding: theme.spacing(1.75, 3),
    gap: theme.spacing(1),
    height: theme.spacing(6),
    ...theme.typography.TextL,
  },

  "&.MuiButton-fullWidth": {
    width: "100%",
  },

  "& .MuiButton-startIcon.MuiButton-iconSizeExtraSmall": {
    marginRight: 0,
    marginLeft: 0,
  },

  "& .MuiButton-startIcon.MuiButton-iconSizeSmall": {
    marginRight: 0,
    marginLeft: "-6px",
  },

  "& .MuiButton-startIcon.MuiButton-iconSizeMedium": {
    marginRight: 0,
    marginLeft: "-6px",
  },

  "& .MuiButton-endIcon.MuiButton-iconSizeExtraSmall": {
    marginLeft: 0,
    marginRight: 0,
  },

  "& .MuiButton-endIcon.MuiButton-iconSizeSmall": {
    marginLeft: 0,
    marginRight: "-6px",
  },

  "& .MuiButton-endIcon.MuiButton-iconSizeMedium": {
    marginLeft: 0,
    marginRight: "-6px",
  },

  "& .MuiSvgIcon-root.MuiSvgIcon-fontSizeExtraSmall": {
    fontSize: "12px",
  },

  // Variants
  "&.MuiButton-primary": {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,

    "&:hover": {
      color: theme.palette.secondary1[60],
    },

    "&:disabled, &.Mui-disabled": {
      opacity: 0.3,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.grey[60],
    },

    "&:focus-visible": {
      outline: `2px solid ${theme.palette.secondary1[60]}`,
    },

    "&:active": {
      color: theme.palette.secondary1[60],
    },

    "&[data-loading]": {
      "& span[data-loader]": {
        backgroundColor: theme.palette.common.white,
      },
    },

    ...theme.applyStyles("dark", {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.secondary1[60],

      "&:hover": {
        backgroundColor: theme.palette.secondary1[10],
      },

      "&:disabled, &.Mui-disabled": {
        backgroundColor: theme.palette.secondary1[180],
      },

      "&:active": {
        backgroundColor: theme.palette.secondary1[100],
      },

      "&[data-loading]": {
        "& span[data-loader]": {
          backgroundColor: theme.palette.common.black,
        },
      },
    }),
  },

  "&.MuiButton-secondary": {
    color: theme.palette.primary.main,
    backgroundColor: "transparent",
    border: `2px solid ${theme.palette.primary.main}`,

    "&:hover": {
      backgroundColor: theme.palette.secondary1[10],
    },

    "&:disabled, &.Mui-disabled": {
      opacity: 0.3,
    },

    "&:focus-visible": {
      outline: `2px solid ${theme.palette.secondary1[100]}`,
    },

    "&:active": {
      backgroundColor: theme.palette.secondary1[10],
    },
  },

  "&.MuiButton-tertiary": {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[20],

    "&:hover": {
      backgroundColor: theme.palette.grey[60],
    },

    "&:disabled, &.Mui-disabled": {
      opacity: 0.3,
      backgroundColor: theme.palette.grey[20],
    },

    "&:focus-visible": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.grey[20],
      outline: `2px solid ${theme.palette.primary.main}`,
    },

    "&:active": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.grey[60],
    },
  },

  "&.MuiButton-text": {
    color: theme.palette.primary.main,
    backgroundColor: "transparent",
    borderRadius: theme.spacing(0.5),

    // Overrides default padding for text buttons (size small and medium)
    padding: theme.spacing(0.5, 1),
    height: "auto",

    "&:hover": {
      color: theme.palette.grey[100],
    },

    "&:disabled, &.Mui-disabled": {
      color: theme.palette.grey[60],
    },

    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
    },

    "&:active": {
      color: theme.palette.grey[100],
    },
  },

  "&.MuiButton-danger": {
    color: theme.palette.error.main,
    border: `2px solid ${theme.palette.error.main}`,

    "&:hover": {
      backgroundColor: theme.palette.error.light,
    },

    "&:disabled, &.Mui-disabled": {
      opacity: 0.3,
      color: theme.palette.primary.main,
      border: `2px solid ${theme.palette.primary.main}`,
    },

    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
    },

    "&:active": {
      backgroundColor: theme.palette.error.light,
    },

    "&[data-loading]": {
      "& span[data-loader]": {
        backgroundColor: theme.palette.error.main,
      },
    },
  },

  "&.MuiButton-link": {
    color: theme.palette.primary.main,
    backgroundColor: "transparent",
    borderRadius: theme.spacing(0.5),

    // Overrides default padding for text buttons (size small and medium)
    padding: theme.spacing(0.5, 1),
    height: "auto",

    "&.MuiButton-sizeSmall": {
      gap: "0px",
    },

    "&:hover": {
      backgroundColor: theme.palette.grey[20],
    },

    "&:disabled, &.Mui-disabled": {
      color: theme.palette.grey[60],
    },

    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
    },

    "&:active": {
      color: theme.palette.grey[100],
    },
  },

  "&.MuiButton-hyrox": {
    color: theme.palette.common.black,
    backgroundColor: "#FFED00",

    "&:hover": {
      backgroundColor: alpha("#FFED00", 0.9),
    },

    "&:disabled, &.Mui-disabled": {
      color: theme.palette.common.white,
      backgroundColor: alpha("#FFED00", 0.3),
    },

    "&:focus-visible": {
      outline: `2px solid ${theme.palette.common.white}`,
    },

    "&:active": {
      backgroundColor: alpha("#FFED00", 0.9),
    },
  },
}));

export default Button;
